export type TriangleMode = 'hypotenuse' | 'angle' | 'cosine';
export type TriangleFocus = 'all' | 'ab' | 'bc' | 'ac' | 'angle' | 'legs';
export interface TriangleStep {
  title: string;
  formula: string;
  explanation: string;
  focus: TriangleFocus;
}
export interface TriangleResult {
  mode: TriangleMode;
  answer: number;
  unit: 'cm' | '°';
  adjacent: number;
  opposite: number;
  third: number;
  angle: number;
  steps: TriangleStep[];
  check: string;
}
/** Significant figures keep small positive lengths visible; calculations never use display text. */
export const triangleNumber = (value: number) =>
  value.toLocaleString('en-AU', { maximumSignificantDigits: 6, useGrouping: false });

export function triangleExplorer(
  mode: TriangleMode,
  draft: Record<string, string>,
): TriangleResult {
  const read = (key: string, label: string, min: number, max: number) => {
    const value = draft[key]?.trim() ? Number(draft[key]) : NaN;
    if (!Number.isFinite(value) || value < min || value > max)
      throw new Error(
        `${label}: enter ${min}–${max}${key === 'angle' ? ' degrees' : ' cm'}. Your entry has not been changed.`,
      );
    return value;
  };
  const a = read('a', mode === 'cosine' ? 'Side a' : 'Adjacent side', 0.1, 1000);
  const b = read('b', mode === 'cosine' ? 'Side b' : 'Opposite side', 0.1, 1000);
  const n = triangleNumber;
  const step = (
    title: string,
    formula: string,
    explanation: string,
    focus: TriangleFocus,
  ): TriangleStep => ({ title, formula, explanation, focus });
  if (mode === 'cosine') {
    const angle = read('angle', 'Included angle C', 0.1, 179.9);
    const radians = (angle * Math.PI) / 180;
    // Equivalent to the cosine rule, without cancellation for nearly equal sides and small angles.
    const third = Math.hypot(a - b, 2 * Math.sqrt(a * b) * Math.sin(radians / 2));
    return {
      mode,
      answer: third,
      unit: 'cm',
      adjacent: b,
      opposite: a,
      third,
      angle,
      steps: [
        step(
          'Choose the relationship',
          'c² = a² + b² − 2ab cos C',
          'We know two sides and their included angle. Side c is opposite C, so use the cosine rule.',
          'all',
        ),
        step(
          'Substitute your numbers',
          `c² = ${a}² + ${b}² − 2 × ${a} × ${b} × cos ${angle}°`,
          'The angle is in degrees. The two known sides meet at C.',
          'angle',
        ),
        step(
          'Calculate the squared length',
          `c² ≈ ${n(a * a)} + ${n(b * b)} − (${n(2 * a * b)} × cos ${angle}°) ≈ ${n(a * a + b * b)} − (${n(2 * a * b * Math.cos(radians))}) ≈ ${n(third * third)} cm²`,
          `cos ${angle}° ≈ ${n(Math.cos(radians))}. Multiply by 2ab, then subtract. Keep full precision during the calculation.`,
          'ac',
        ),
        step(
          'Take the positive square root',
          `c = √(a² + b² − 2ab cos C) ≈ ${n(third)} cm`,
          'A length is positive. This gives the side marked c in your diagram.',
          'bc',
        ),
      ],
      check: `The unrounded lengths satisfy |a − b| < c < a + b. The third side is between the difference and the sum of the known sides.`,
    };
  }
  const hypotenuse = Math.hypot(a, b);
  const angle = (Math.atan2(b, a) * 180) / Math.PI;
  if (mode === 'angle')
    return {
      mode,
      answer: angle,
      unit: '°',
      adjacent: a,
      opposite: b,
      third: hypotenuse,
      angle,
      steps: [
        step(
          'Identify the sides beside θ',
          `Adjacent = ${a} cm; opposite = ${b} cm`,
          'Adjacent and opposite are named relative to θ. The hypotenuse is across from the right angle.',
          'angle',
        ),
        step(
          'Use the tangent ratio',
          `tan θ = opposite ÷ adjacent = ${b} ÷ ${a}`,
          'Tangent connects these two known sides. The units cancel in the ratio.',
          'bc',
        ),
        step(
          'Find the angle',
          `θ = arctan(${b} ÷ ${a}) ≈ ${n(angle)}°`,
          'Inverse tangent turns the original ratio into an angle. Use degree mode; do not round the ratio first.',
          'angle',
        ),
        step(
          'Check the angle',
          `Other acute angle ≈ ${n(90 - angle)}°`,
          'The two acute angles add to 90°. The larger opposite side gives the larger angle.',
          'all',
        ),
      ],
      check: `0° < θ < 90°. Using the unrounded angle, ${a} × tan θ ≈ ${b} cm, which recovers the opposite side.`,
    };
  return {
    mode,
    answer: hypotenuse,
    unit: 'cm',
    adjacent: a,
    opposite: b,
    third: hypotenuse,
    angle,
    steps: [
      step(
        'Choose Pythagoras',
        'c² = a² + b²',
        'These sides meet at a right angle. The hypotenuse c is opposite that angle.',
        'all',
      ),
      step(
        'Square and add your sides',
        `c² = ${a}² + ${b}² ≈ ${n(a * a)} + ${n(b * b)} ≈ ${n(a * a + b * b)} cm²`,
        'Squaring gives areas. Add the two smaller square areas to get the square area on c.',
        'legs',
      ),
      step(
        'Take the positive square root',
        `c = √(${a}² + ${b}²) ≈ √(${n(a * a + b * b)}) ≈ ${n(hypotenuse)} cm`,
        'The square root changes the squared length back into a length. Choose the positive root.',
        'ac',
      ),
      step(
        'Check the length',
        'c > max(a, b) for the unrounded lengths',
        'The hypotenuse must be longer than either straight side. Substitute the unrounded result into c² = a² + b².',
        'all',
      ),
    ],
    check: `The original squared sides total approximately ${n(a * a + b * b)} cm². The unrounded c² matches this total.`,
  };
}
